/**
 * Procedural Rounded Concrete Patio — poured concrete ground plate.
 *
 * Footprint: rectangle with ONLY the two upper corners rounded (square bottom).
 * Surface regenerates from width/height so control joints reflow instead of
 * stretching a static PNG.
 */

export const ROUNDED_CONCRETE_PATIO_PROCEDURAL = 'rounded-concrete-patio' as const;

export type GroundProceduralKind = typeof ROUNDED_CONCRETE_PATIO_PROCEDURAL;

/** Target slab size — sparse strike lines (a couple, not a grid). */
const TARGET_SLAB_WIDTH_FRAC = 0.2;
const TARGET_SLAB_HEIGHT_FRAC = 0.15;
/** Cap so even huge pads only get a couple of joints. */
const MAX_JOINT_COLS = 3; // → at most 2 vertical strike lines
const MAX_JOINT_ROWS = 2; // → at most 1 horizontal strike line
/** Corner radius in world-width fraction — stable across resize. */
const CORNER_RADIUS_WORLD_FRAC = 0.028;

const REF_WORLD_WIDTH_PX = 1600;

export interface RoundedConcretePatioPaintInput {
  /** World width fraction (0–1). */
  widthFrac: number;
  /** World height fraction (0–1). */
  heightFrac: number;
  /** World width / height. */
  worldAspect: number;
  /**
   * `rounded-top` bakes the silhouette into the canvas (soft-stretch).
   * `rect` fills the box; freeform clip-path supplies top-rounded sides.
   */
  silhouette?: 'rounded-top' | 'rect';
}

export interface RoundedConcretePatioPaintResult {
  dataUrl: string;
  imageWidth: number;
  imageHeight: number;
  jointCols: number;
  jointRows: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Joint column count from authored width (reflows on resize). */
export function concreteJointCols(widthFrac: number): number {
  return clamp(
    Math.round(widthFrac / TARGET_SLAB_WIDTH_FRAC),
    1,
    MAX_JOINT_COLS,
  );
}

/** Joint row count from authored height (reflows on resize). */
export function concreteJointRows(heightFrac: number): number {
  return clamp(
    Math.round(heightFrac / TARGET_SLAB_HEIGHT_FRAC),
    1,
    MAX_JOINT_ROWS,
  );
}

function canvasSize(
  widthFrac: number,
  heightFrac: number,
  worldAspect: number,
): { widthPx: number; heightPx: number; radiusPx: number } {
  const aspect = worldAspect > 0 ? worldAspect : 16 / 9;
  const rawW = widthFrac * REF_WORLD_WIDTH_PX;
  const rawH = heightFrac * (REF_WORLD_WIDTH_PX / aspect);
  // Quantize so live resize doesn't regenerate every pixel tick.
  const widthPx = clamp(Math.round(rawW / 16) * 16, 160, 2400);
  const heightPx = clamp(Math.round(rawH / 16) * 16, 96, 1800);
  const radiusPx = clamp(
    CORNER_RADIUS_WORLD_FRAC * REF_WORLD_WIDTH_PX,
    18,
    Math.min(widthPx * 0.22, heightPx * 0.45),
  );
  return { widthPx, heightPx, radiusPx };
}

/** Path: square bottom corners, rounded top-left + top-right only. */
export function traceRoundedTopRect(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h);
  ctx.beginPath();
  ctx.moveTo(0, radius);
  ctx.arcTo(0, 0, radius, 0, radius);
  ctx.lineTo(w - radius, 0);
  ctx.arcTo(w, 0, w, radius, radius);
  ctx.lineTo(w, h);
  ctx.lineTo(0, h);
  ctx.closePath();
}

function fillConcreteBody(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): void {
  // Warm light gray / taupe base.
  const base = ctx.createLinearGradient(0, 0, 0, h);
  base.addColorStop(0, '#d9d2c6');
  base.addColorStop(0.45, '#cfc6b8');
  base.addColorStop(1, '#c4baab');
  ctx.fillStyle = base;
  ctx.fill();

  // Soft top rim highlight.
  ctx.save();
  ctx.clip();
  const rim = ctx.createLinearGradient(0, 0, 0, Math.min(28, h * 0.12));
  rim.addColorStop(0, 'rgba(255,252,245,0.35)');
  rim.addColorStop(1, 'rgba(255,252,245,0)');
  ctx.fillStyle = rim;
  ctx.fillRect(0, 0, w, Math.min(28, h * 0.12));

  // Subtle mottled grain (storybook, not photoreal).
  const dots = Math.floor((w * h) / 180);
  for (let i = 0; i < dots; i++) {
    const x = (Math.sin(i * 12.9898) * 43758.5453) % 1;
    const y = (Math.sin(i * 78.233) * 12345.6789) % 1;
    const px = Math.abs(x) * w;
    const py = Math.abs(y) * h;
    const shade = 0.5 + ((Math.sin(i * 3.1) + 1) / 2) * 0.5;
    const a = 0.035 + shade * 0.04;
    ctx.fillStyle =
      shade > 0.55
        ? `rgba(255,250,240,${a})`
        : `rgba(110,95,80,${a * 0.9})`;
    ctx.fillRect(px, py, 1.6, 1.6);
  }
  ctx.restore();
}

function strokeControlJoints(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  cols: number,
  rows: number,
  radius: number,
): void {
  ctx.save();
  traceRoundedTopRect(ctx, w, h, radius);
  ctx.clip();

  const inset = Math.max(6, Math.min(w, h) * 0.02);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Vertical joints (skip outer edges).
  for (let c = 1; c < cols; c++) {
    const x = (c / cols) * w;
    ctx.beginPath();
    ctx.moveTo(x, inset);
    ctx.lineTo(x, h - inset);
    ctx.strokeStyle = 'rgba(120,108,92,0.22)';
    ctx.lineWidth = 3.2;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, inset);
    ctx.lineTo(x, h - inset);
    ctx.strokeStyle = 'rgba(90,80,68,0.34)';
    ctx.lineWidth = 1.35;
    ctx.stroke();
    // Soft highlight on one side of the groove.
    ctx.beginPath();
    ctx.moveTo(x + 1.1, inset);
    ctx.lineTo(x + 1.1, h - inset);
    ctx.strokeStyle = 'rgba(255,250,240,0.18)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Horizontal joints.
  for (let r = 1; r < rows; r++) {
    const y = (r / rows) * h;
    ctx.beginPath();
    ctx.moveTo(inset, y);
    ctx.lineTo(w - inset, y);
    ctx.strokeStyle = 'rgba(120,108,92,0.2)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(inset, y);
    ctx.lineTo(w - inset, y);
    ctx.strokeStyle = 'rgba(90,80,68,0.32)';
    ctx.lineWidth = 1.25;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(inset, y + 1.1);
    ctx.lineTo(w - inset, y + 1.1);
    ctx.strokeStyle = 'rgba(255,250,240,0.16)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.restore();
}

function strokeSoftEdge(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  radius: number,
  silhouette: 'rounded-top' | 'rect',
): void {
  ctx.save();
  if (silhouette === 'rounded-top') {
    traceRoundedTopRect(ctx, w, h, radius);
  } else {
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
  }
  ctx.strokeStyle = 'rgba(95,85,72,0.28)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,250,240,0.22)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

/**
 * Paint a rounded-top concrete patio into a canvas / data URL.
 * Returns null when canvas is unavailable (e.g. some test hosts).
 */
export function paintRoundedConcretePatio(
  input: RoundedConcretePatioPaintInput,
): RoundedConcretePatioPaintResult | null {
  if (typeof document === 'undefined') return null;
  const widthFrac = Math.max(input.widthFrac, 0.02);
  const heightFrac = Math.max(input.heightFrac, 0.015);
  const silhouette = input.silhouette ?? 'rounded-top';
  const { widthPx, heightPx, radiusPx } = canvasSize(
    widthFrac,
    heightFrac,
    input.worldAspect,
  );
  const cols = concreteJointCols(widthFrac);
  const rows = concreteJointRows(heightFrac);

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.clearRect(0, 0, widthPx, heightPx);
  if (silhouette === 'rounded-top') {
    traceRoundedTopRect(ctx, widthPx, heightPx, radiusPx);
  } else {
    ctx.beginPath();
    ctx.rect(0, 0, widthPx, heightPx);
  }
  fillConcreteBody(ctx, widthPx, heightPx);
  strokeControlJoints(
    ctx,
    widthPx,
    heightPx,
    cols,
    rows,
    silhouette === 'rounded-top' ? radiusPx : 0,
  );
  strokeSoftEdge(ctx, widthPx, heightPx, radiusPx, silhouette);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    imageWidth: widthPx,
    imageHeight: heightPx,
    jointCols: cols,
    jointRows: rows,
  };
}

/** Per-layer cache so live resize can reuse identical quantized paints. */
const paintCache = new Map<
  string,
  { fingerprint: string; result: RoundedConcretePatioPaintResult }
>();

export function resolveRoundedConcretePatioSrc(
  layerId: string,
  input: RoundedConcretePatioPaintInput,
  fallbackSrc: string,
): { src: string; imageWidth?: number; imageHeight?: number } {
  const widthFrac = Math.max(input.widthFrac, 0.02);
  const heightFrac = Math.max(input.heightFrac, 0.015);
  const silhouette = input.silhouette ?? 'rounded-top';
  const { widthPx, heightPx } = canvasSize(
    widthFrac,
    heightFrac,
    input.worldAspect,
  );
  const cols = concreteJointCols(widthFrac);
  const rows = concreteJointRows(heightFrac);
  const fingerprint = `${widthPx}x${heightPx}:${cols}x${rows}:${silhouette}`;
  const cached = paintCache.get(layerId);
  if (cached?.fingerprint === fingerprint) {
    return {
      src: cached.result.dataUrl,
      imageWidth: cached.result.imageWidth,
      imageHeight: cached.result.imageHeight,
    };
  }
  const painted = paintRoundedConcretePatio({ ...input, silhouette });
  if (!painted) {
    return { src: fallbackSrc };
  }
  paintCache.set(layerId, { fingerprint, result: painted });
  return {
    src: painted.dataUrl,
    imageWidth: painted.imageWidth,
    imageHeight: painted.imageHeight,
  };
}

export function isRoundedConcretePatioProcedural(
  kind: string | undefined | null,
): boolean {
  return kind === ROUNDED_CONCRETE_PATIO_PROCEDURAL;
}
